import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { StarIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { BriefcaseIcon, ClockIcon } from "@heroicons/react/outline";
import { calculateExperience, isWithinWorkingHours } from "../../Utils";

function getInitials(fullName = "User") {
  var allNames = fullName.trim().split(" ");
  var initials = allNames.reduce((acc, curr, index) => {
    if (index === 0 || index === allNames.length - 1) {
      acc = `${acc}${curr.charAt(0).toUpperCase()}`;
    }
    return acc;
  }, "");
  return initials.toUpperCase();
}

const Consultant = ({ consultant }) => {
  const { t } = useTranslation();
  const [imageError, setimageError] = useState(false);

  const experienceYear = Math.max(1, calculateExperience(consultant));

  const services = consultant.services?.items || [];

  // Online indicator
  const isOnline = isWithinWorkingHours(
    consultant.startTimes,
    consultant.endTimes,
    "Asia/Jakarta",
    consultant.activeDays,
  );

  // Find top service: first topServices entry that is offered AND not 'general'
  const offeredServiceIds = new Set(services.map((s) => s.serviceID));
  const topServiceId =
    (consultant.topServices || []).find((id) => {
      if (!offeredServiceIds.has(id)) return false;
      return id.toLowerCase() !== "general";
    }) || null;

  const formatAverageResponseTime = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return null;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0)
      return `${hours} ${t("hours")} ${minutes} ${t("minutes")}`;
    if (hours > 0) return `${hours} ${t("hours")}`;
    if (minutes > 0) return `${minutes} ${t("minutes")}`;
    return null;
  };

  const responseTime = formatAverageResponseTime(consultant.averageResponse);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
      {/* Header: Avatar + Name + Stats + Chevron */}
      <div className="flex items-center px-4 py-4 gap-3">
        {/* Avatar with online dot */}
        <div className="relative flex-shrink-0">
          {consultant.user?.imageUrl && !imageError ? (
            <img
              src={consultant.user.imageUrl}
              alt="Profile"
              className="w-12 h-12 rounded-full object-cover"
              onError={() => setimageError(true)}
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-base">
              {getInitials(consultant.user?.name || "Konsultan")}
            </div>
          )}
          {/* Online indicator dot */}
          <span
            className={`absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-white ${
              isOnline ? "bg-green-500" : "bg-slate-400"
            }`}
          />
        </div>

        {/* Name + Stats */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {consultant.user?.name || "Konsultan"}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
            {consultant.rating ? (
              <>
                <StarIcon className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                <span className="font-medium text-slate-700">
                  {Number(consultant.rating).toFixed(2)}
                </span>
                {consultant.totalConsultation > 0 && (
                  <span>({consultant.totalConsultation})</span>
                )}
              </>
            ) : null}
            {consultant.totalMonthlyConsultation > 0 && (
              <span>
                {consultant.rating ? "| " : ""}
                {consultant.totalMonthlyConsultation}+ {t("clientsPerMonth")}
              </span>
            )}
          </div>
        </div>

        {/* Chevron */}
        <ChevronRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </div>

      {/* Divider */}
      <hr className="border-gray-100" />

      {/* Specialization Chips */}
      {services.length > 0 && (
        <>
          <div className="px-4 py-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
              {t("Specialization")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {services.map((service) => {
                const isTop = service.serviceID === topServiceId;
                return (
                  <span
                    key={service.id}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                      isTop
                        ? "bg-yellow-50 border-yellow-300 text-yellow-800"
                        : "bg-blue-50 border-blue-200 text-blue-700"
                    }`}
                  >
                    {isTop && <span>🏆</span>}
                    {service.service?.name || t(service.serviceID)}
                  </span>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Footer: Experience + Response + Price */}
      <hr className="border-gray-100 mt-auto" />
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex flex-col gap-1">
          {/* Experience */}
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <BriefcaseIcon className="w-4 h-4 text-slate-400" />
            <span>
              {experienceYear} {t("Years of Experience")}
            </span>
          </div>

          {/* Response time */}
          {responseTime && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <ClockIcon className="w-4 h-4 text-slate-400" />
              <span>
                {t("Replied")} ±{responseTime}
              </span>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="text-right ml-3">
          <p className="text-xs text-slate-400">{t("startingFrom")}</p>
          <p className="text-base font-bold text-blue-600">
            {consultant.consultationPrice > 0 ? (
              <NumericFormat
                displayType={"text"}
                value={consultant.consultationPrice}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"Rp"}
              />
            ) : (
              t("Not Available")
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Consultant;
